import PRESETS from "./MEGA_SEARCH_PRESETS.json";

/* ================= helpers ================= */

const normMarket = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\s*\+\s*/g, "+") // "Hits + Runs + RBIs" -> "hits+runs+rbis"
    .replace(/\s+/g, " ")
    .trim();

export const bucketizeConfidence = (v) => {
  const x = Number(v);
  if (!Number.isFinite(x)) return "";
  if (x < 0.25) return "Low (<0.25)";
  if (x < 0.50) return "Moderate (0.25-0.5)";
  if (x < 0.75) return "Strong (0.5-0.75)";
  if (x <= 1.00) return "Elite (0.75-1)";
  return "Volatile Elite (>1)";
};

const normTier = (s) => {
  const t = String(s || "").trim().toLowerCase();
  if (!t) return "";
  if (/very\s*high|target/i.test(t)) return "Elite"; // your “Very High (Target)”
  if (/^high\b/i.test(t))           return "Great";
  if (/elite/i.test(t))             return "Elite";
  if (/\bgreat\b/i.test(t))         return "Great";
  if (/\bgood\b/i.test(t))          return "Good";
  if (/below\s*average/i.test(t))   return "Below Average";
  if (/\baverage\b/i.test(t))       return "Average";
  if (/\bpoor\b|\blow\b/i.test(t))  return "Poor";
  return String(s || "");
};

const normHA = (v) => {
  const s = String(v || "").trim().toUpperCase();
  if (s === "H") return "HOME";
  if (s === "A") return "AWAY";
  return s; // "HOME"/"AWAY"/""
};

const includesOrMissing = (allowed, value) =>
  !allowed || allowed.length === 0 || !value || allowed.includes(value);

/* ================ row normalization ================ */

export const normalizeRow = (p) => {
  const confBucket =
    String(p.confBucket ?? bucketizeConfidence(p.Confidence ?? p.confidence ?? null));

  // market / prop type
  const marketRaw  = String(p["Prop Type"] ?? p.market ?? "").trim();
  const marketNorm = normMarket(marketRaw);

  // opp pitcher hand -> "RHP"/"LHP"/""
  const handChar = (
    (p.opp_hand ??
      p.opp_pitcher_hand ??
      p.opp_pitch_hand ??
      p["Opp Pitcher Hand"] ??
      p["OppPitcherThrows"] ??
      p["Opp Hand"] ??
      "") + ""
  ).toUpperCase().slice(0, 1);
  const oppHand = handChar === "R" ? "RHP" : handChar === "L" ? "LHP" : "";

  // batting order number
  const bo = Number(
    p.battingOrder ?? p.bat_order ?? p["BattingOrder"] ?? p["Bat Order"] ?? p.bat_order_num
  );

  // tiers
  const whipTier = normTier(
    p.opp_whip_tier ?? p.team_whip_tier ??
    p.oppWhipTier ?? p.teamWhipTier ??
    p["Opponent WHIP Tier"] ?? p["Team WHIP Tier"]
  );

  const attackTier = normTier(
    p.opp_pitcher_attackability ?? p.opp_attackability ?? p.opp_attack_tier ??
    p.attackabilityTier ?? p["Opponent Attackability"] ?? p["Opponent Attack Tier"]
  );

  // Home/Away
  const homeAway = normHA(p["Home/Away"] ?? p.home_away ?? p.venue);

  // side from Direction or Tag (fallback)
  let side = String(p.Direction ?? p.side ?? "").toUpperCase();
  const tagUpper = String(p.Tag ?? "").toUpperCase();
  if (!side) {
    if (tagUpper.includes("FADE") || tagUpper.includes("UNDER")) side = "UNDER";
    else if (tagUpper.includes("SMASH") || tagUpper.includes("OVER")) side = "OVER";
  }
  const isMegaFadeTag = tagUpper.includes("MEGA FADE");

  // opponent ERA (for a simple quality brake)
  const oppERA = Number(
    p.opp_era ?? p["Opp ERA"] ?? p["opp_era"] ?? p["opp_era_val"] ?? p["opp_era_num"]
  );

  return {
    confBucket, marketNorm, oppHand, bo, whipTier, attackTier, homeAway, side,
    oppERA, isMegaFadeTag
  };
};

/* ================= matching ================= */

export const passesPreset = (row, preset) => {
  const f = normalizeRow(row);

  // ---------- UNDER guardrails ----------
  // Skip guardrails if this row is explicitly tagged MEGA FADE,
  // or if the preset opts out via allow_* flags.
  if (preset.side === "UNDER" && !f.isMegaFadeTag) {
    const allowAttackable = preset.allow_attackable === true;
    const allowHighERA    = preset.allow_high_era === true;
    const allowHomeCore   = preset.allow_home_core === true;

    // 1) Don’t fade vs attackable SP (Elite/Great) unless allowed
    if (!allowAttackable && (f.attackTier === "Elite" || f.attackTier === "Great")) {
      return false;
    }
    // 2) Don’t fade when ERA is truly poor unless allowed
    if (!allowHighERA && Number.isFinite(f.oppERA) && f.oppERA >= 5.00) {
      return false;
    }
    // 3) Don’t fade top-of-order at HOME (extra PA + park boost) unless allowed
    if (!allowHomeCore && f.homeAway === "HOME" && Number.isFinite(f.bo) && f.bo <= 4) {
      return false;
    }
  }
  // ---------- end guardrails ----------

  // normalize preset markets once
  const presetMarkets = (preset.market_any_of || []).map(normMarket);

  const marketOK = !preset.market_any_of || presetMarkets.includes(f.marketNorm);
  const confOK   = !preset.confidence || f.confBucket === preset.confidence;
  const handOK   = !preset.opp_hand || preset.opp_hand[0] === (f.oppHand || "").charAt(0);
  const boOK     =
    !preset.bat_order_any_of || (Number.isFinite(f.bo) && preset.bat_order_any_of.includes(f.bo));
  const whipOK   = includesOrMissing(preset.opp_whip_tiers,   f.whipTier);
  const atkOK    = includesOrMissing(preset.opp_attack_tiers, f.attackTier);
  const haOK     = !preset.home_away || !f.homeAway || preset.home_away === f.homeAway;
  const sideOK   = !preset.side || preset.side === f.side;

  return marketOK && confOK && handOK && boOK && whipOK && atkOK && haOK && sideOK;
};

/* ================= apply ================= */

export const applyGuru = (rows, type = "overs") => {
  const t = (type || "").toLowerCase();
  const active = t === "all" ? [...PRESETS.overs, ...PRESETS.unders] : PRESETS[t] || [];
  return rows.filter((r) => active.some((p) => passesPreset(r, p)));
};
